/**
 * AccountManagerChat — Client-side AM chat component
 *
 * Replaces the "Coming Soon" placeholder in Messages.tsx.
 * Provides real-time chat between a client (agent) and their assigned AM.
 */

import { useState, useEffect, useRef } from "react";
import { Send, Loader2, Headphones, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAMMessages, AMMessage } from "@/hooks/useAMMessages";
import { supabase } from "@/integrations/supabase/client";
import { useAgent } from "@/contexts/AgentContext";
import { toast } from "sonner";

interface AccountManagerChatProps {
  amId: string;
  amName: string;
  amInitials: string;
}

function formatMessageTime(ts: string): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  if (diffDays === 0) return time;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) {
    return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  }
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

function ReadIndicator({ msg }: { msg: AMMessage }) {
  if (msg.sender_role !== "client") return null;
  if (msg.read_at) {
    return <CheckCheck className="w-3 h-3 text-blue-500" />;
  }
  // Message exists in DB = delivered; show single check until read
  return <Check className="w-3 h-3 text-muted-foreground" />;
}

export default function AccountManagerChat({
  amId,
  amName,
  amInitials,
}: AccountManagerChatProps) {
  const { state: agentState } = useAgent();
  const agentId = agentState.profile.id;

  const [userId, setUserId] = useState<string>("");
  const [body, setBody] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setUserId(data.user.id);
    });
  }, []);

  const { messages, loading, sending, sendMessage, markAsRead, unreadCount } =
    useAMMessages({
      amId,
      agentId,
      senderRole: "client",
      senderId: userId,
      enabled: !!amId && !!agentId && !!userId,
    });

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (unreadCount > 0) {
      markAsRead();
    }
  }, [unreadCount, markAsRead]);

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;

    const result = await sendMessage(trimmed);
    if (result) {
      setBody("");
    } else {
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
            <Headphones className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium text-foreground">{amName}</p>
            <p className="text-xs text-muted-foreground">
              Trusted James Support
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="space-y-4">
            {/* Welcome message from AM */}
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0 text-xs font-semibold">
                {amInitials}
              </div>
              <div className="max-w-[75%]">
                <div className="inline-block px-4 py-3 rounded-2xl rounded-tl-none bg-muted text-foreground">
                  <p className="text-sm whitespace-pre-wrap">
                    Hi! I'm {amName}, your dedicated account manager at Trusted
                    James. Feel free to reach out anytime — I'm here to help
                    with leads, billing, delivery optimization, and anything else
                    you need.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {amName}
                </p>
              </div>
            </div>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.sender_role === "client" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.sender_role === "am" && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground flex-shrink-0 text-xs font-semibold mr-2 mt-1">
                  {amInitials}
                </div>
              )}
              <div
                className={`max-w-[75%] rounded-xl px-3.5 py-2.5 ${
                  msg.sender_role === "client"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.body}
                </p>
                <div
                  className={`flex items-center gap-1 mt-1 ${
                    msg.sender_role === "client"
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <span
                    className={`text-[10px] ${
                      msg.sender_role === "client"
                        ? "text-primary-foreground/60"
                        : "text-muted-foreground"
                    }`}
                  >
                    {formatMessageTime(msg.created_at)}
                  </span>
                  <ReadIndicator msg={msg} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Compose */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Textarea
            placeholder={`Message ${amName}...`}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            onKeyDown={handleKeyDown}
            className="min-h-[44px] max-h-32 resize-none text-sm"
            rows={1}
            maxLength={2000}
            disabled={sending}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={sending || !body.trim()}
            className="h-[44px] w-[44px] flex-shrink-0"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        {body.trim() && (
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Ctrl+Enter to send &middot; {body.length}/2000
          </p>
        )}
      </div>
    </>
  );
}
